/**
 * Refus « stock insuffisant » du déclencheur `update_product_stock_from_movement`
 * (migration 20260928180000) : `INSUFFICIENT_STOCK {json}`.
 *
 * ⚠️ Le JSON vit DANS LE MESSAGE, pas dans `details` : sur une facture, la
 *    déduction est non bloquante et `save_invoice` ne renvoie que `SQLERRM`
 *    dans `inventory_error`. Ce module lit donc aussi bien une erreur
 *    PostgREST (`{ message }`) qu'une chaîne nue.
 */
export type InsufficientStock = {
  productName: string;
  locationName: string | null;
  available: number;
};

const PREFIX = "INSUFFICIENT_STOCK ";

export function parseInsufficientStock(text: unknown): InsufficientStock | null {
  if (typeof text !== "string" || !text.startsWith(PREFIX)) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(text.slice(PREFIX.length));
  } catch {
    return null;
  }
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const productName = typeof r.product_name === "string" ? r.product_name.trim() : "";
  if (!productName) return null;
  if (typeof r.available !== "number" || !Number.isFinite(r.available)) return null;
  const locationName =
    typeof r.location_name === "string" && r.location_name.trim() ? r.location_name.trim() : null;
  return { productName, locationName, available: r.available };
}

export function formatInsufficientStock(
  stock: InsufficientStock,
  translate: (key: string) => string,
): string {
  const template = translate(
    stock.locationName ? "errors.supabase.insufficientStockAt" : "errors.supabase.insufficientStockNoLocation",
  );
  const values: Record<string, string> = {
    product: stock.productName,
    location: stock.locationName ?? "",
    // Borné à 0 à l'affichage : un stock déjà négatif (réglage « stock négatif »
    // désactivé après des ventes sous zéro) donnerait « il en reste -2 ».
    available: String(Math.max(0, stock.available)),
  };
  // Une seule passe, avec une fonction de remplacement : un nom qui contient
  // « {location} » ou « $& » n'est ni re-substitué ni interprété.
  return template.replace(/\{(product|location|available)\}/g, (_, key: string) => values[key]);
}

export function insufficientStockMessage(
  errorOrText: unknown,
  translate: (key: string) => string,
): string | null {
  const text =
    errorOrText && typeof errorOrText === "object"
      ? (errorOrText as { message?: unknown }).message
      : errorOrText;
  const stock = parseInsufficientStock(text);
  return stock ? formatInsufficientStock(stock, translate) : null;
}
